import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Box, MoreVertical, Pencil, Share2, Trash2, Check, X } from "lucide-react";

import { Room, RoomVisibility } from "@/types/rooms";
import { RoomShareDialog } from "./RoomShareDialog";
import { RoomDeleteDialog } from "./RoomDeleteDialog";

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

interface Props {
  room: Room;
  onRename: (id: string, name: string) => Promise<void> | void;
  onDelete: (id: string) => Promise<void> | void;
  onChangeVisibility: (id: string, visibility: RoomVisibility) => Promise<void> | void;
}

export function RoomCard({
  room,
  onRename,
  onDelete,
  onChangeVisibility,
}: Props) {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(room.name);
  const [shareOpen, setShareOpen] = React.useState(false);

  const handleSave = async () => {
    if (!draft.trim() || draft === room.name) {
      setIsEditing(false);
      return;
    }
    await onRename(room.id, draft.trim());
    setIsEditing(false);
  };

  const handleCancel = () => {
    setDraft(room.name);
    setIsEditing(false);
  };

  return (
    <div
      className="text-card-foreground bg-card border-2 border-border rounded-2xl shadow-sm cursor-pointer group overflow-hidden transition-all hover:border-border/80"
      onClick={() => !isEditing && navigate(`/room/${room.id}`)}
    >
      {/* Top Icon + Menu */}
      <div className="h-14 relative flex items-center justify-end px-4">
        <div className="absolute inset-0 bg-primary/5" />
        <div className="absolute bottom-0 left-4 translate-y-1/2 z-10">
          <div className="p-1 rounded-full bg-background">
            <Box className="w-5 h-5 text-primary" />
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            asChild
          >
            <button className="relative z-20 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full bg-background hover:bg-muted">
              <MoreVertical className="w-3.5 h-3.5 text-foreground" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            className="z-50 bg-popover"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={() => setShareOpen(true)}
            >
              <Share2 className="w-4 h-4" /> Share
            </DropdownMenuItem>

            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="w-4 h-4" /> Edit
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <RoomDeleteDialog
              room={room}
              onDelete={() => onDelete(room.id)}
            >
              <DropdownMenuItem
                className="flex items-center gap-2 text-destructive focus:text-destructive"
                onSelect={(e) => e.preventDefault()}
              >
                <Trash2 className="w-4 h-4" /> Delete
              </DropdownMenuItem>
            </RoomDeleteDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Body */}
      <div className="px-4 pt-5 pb-4">
        {!isEditing ? (
          <>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate tracking-wide text-foreground/80 group-hover:text-foreground flex-1">
                {room.name}
              </span>
              <span className="text-xs text-muted-foreground/70">
                {room.content_count} content
              </span>
            </div>

            <div className="mt-1 text-xs text-muted-foreground/70 italic">
              {room.description || "Create a course to track progress"}
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="h-8 text-sm"
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") handleCancel();
              }}
            />
            <button
              className="text-success hover:text-success/80"
              onClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              className="text-destructive hover:text-destructive/80"
              onClick={(e) => {
                e.stopPropagation();
                handleCancel();
              }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Share Dialog - rendered outside card click area */}
      <div onClick={(e) => e.stopPropagation()}>
      <RoomShareDialog
        room={room}
        open={shareOpen}
        onOpenChange={setShareOpen}
        onVisibilityChange={(v) => onChangeVisibility(room.id, v)}
      />
      </div>
    </div>
  );
}
